import { useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { useIsDarkMode } from "@/hooks/useIsDarkMode";

const Terms = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isDarkMode = useIsDarkMode();

  return (
    <div className={`min-h-screen flex ${isDarkMode ? 'bg-[#000000] text-white' : 'bg-white text-black'}`}>
      <Sidebar
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        onAuth={() => { }}
        user={null}
        onNewChat={() => { }}
        isDarkMode={isDarkMode}
      />

      <div className="flex-1 lg:ml-16 transition-all duration-300">
        <div className="max-w-[800px] mx-auto px-6 py-20 lg:py-24 font-sans">

          {/* Header */}
          <div className="mb-16 text-center sm:text-left">
            <p className={`text-sm font-medium mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Updated: November 14, 2025
            </p>
            <h1 className="text-5xl sm:text-6xl font-normal tracking-tight mb-2">
              Terms & Conditions
            </h1>
          </div>


          {/* Content */}
          <div className={`space-y-12 text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>1. Introduction</h2>
              <p>
                Welcome to Oreog ("the Platform"). Oreog is owned and operated as a brand of Oreog Labs Inc., a company registered in the State of Delaware, USA ("we," "our," or "us").
              </p>
              <p>
                By accessing or using Oreog via our application or any of our related services, you ("User," "you") agree to be bound by these Terms & Conditions, our Privacy Policy, and any additional policies we may introduce.
              </p>
              <p>
                If you do not agree, you must discontinue use immediately.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>2. Services Provided</h2>
              <p>
                Oreog provides access to premium artificial intelligence services, including but not limited to interactions with AI platforms such as Grok, ChatGPT, Gemini, DeepSeek, Claude, Perplexity, as well as AI-powered tools for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Prompt Enhancement</li>
                <li>Image Generation (limited models based on availability)</li>
                <li>Audio Transcription</li>
                <li>Sudoku and other interactive games</li>
              </ul>
              <p>
                Your subscription options determine your access limits and features.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>3. Subscription Plans & Pricing</h2>
              <p>
                We offer subscription plans including Free, Go, Plus, and Pro tiers. Details of pricing and features are available on our Pricing page.
              </p>
              <p>
                Prices are inclusive of applicable taxes. We reserve the right to change subscription prices with prior notice.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>4. Payment Processing</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>For Customers Outside India:</strong> Payments are processed by TagMango Inc.</li>
                <li><strong>For Customers in India:</strong> Payments are facilitated by TagMango Private Limited.</li>
              </ul>
              <p>
                Your billing information must be accurate and up-to-date. Failed or non-processed payments may result in suspension or cancellation of your account.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>5. Refund & Cancellation Policy</h2>
              <p>
                All payments made to Oreog are non-refundable, regardless of your usage during the subscription period.
              </p>
              <p>
                This means that once your subscription fee is charged, you will not be entitled to a refund for any unused services, unused tokens, or partial subscription periods.
              </p>
              <p>
                You may cancel your subscription at any time; however, cancellation will only prevent future billing charges and will not result in any refund for the current billing cycle.
              </p>
              <p>
                Exceptions to this policy may only be granted if required by applicable law.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>6. Token Usage Policy</h2>
              <p><strong>Definition of Tokens</strong></p>
              <p>
                In the context of Oreog, a "token" refers to a unit of text-processing capacity used to interact with AI models on our platform. Tokens are consumed when you send messages or prompts to AI models and when the AI generates responses back to you. The number of tokens consumed depends on the length and complexity of the prompt and the output generated.
              </p>
              <p><strong>Monthly Token Allocation</strong></p>
              <p>
                Different plans have different token allocations or limits. Unused tokens do not roll over to the next cycle unless expressly stated in writing by Oreog.
              </p>
              <p><strong>No Cash Value</strong></p>
              <p>
                Tokens have no monetary or cash value, cannot be refunded, exchanged, transferred, resold, or converted into currency or credit of any kind. Tokens exist solely for use within the Oreog platform during your active subscription term.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>7. Intellectual Property</h2>
              <p>
                All materials, brand assets, Oreog's name, logo, and proprietary prompt library are the intellectual property of Oreog Labs Inc. You may not reproduce, redistribute, or sell our materials or tools without express written consent.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>8. Restrictions on Use</h2>
              <p>
                You may not:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use Oreog for activities that break any law or regulation in your jurisdiction.</li>
                <li>Attempt to reverse-engineer, scrape, or modify the platform's backend.</li>
                <li>Share your account credentials or resell access to Oreog without authorization.</li>
                <li>Generate or disseminate harmful, illegal, or discriminatory content.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>9. Acceptable Use Policy (AUP)</h2>
              <p>
                When using Oreog, you agree NOT to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the service in violation of any applicable law, regulation, or third-party rights.</li>
                <li>Generate, upload, or share content that is illegal, harmful, abusive, hateful, discriminatory, or obscene.</li>
                <li>Generate misinformation intended to deceive or cause harm.</li>
                <li>Content infringing intellectual property, privacy, or publicity rights.</li>
                <li>Attempt to bypass platform usage limits or manipulate token allocations.</li>
                <li>Reverse-engineer, decompile, or interfere with the operation of Oreog or any integrated AI provider's systems.</li>
                <li>Share, resell, or lease your account access without written permission.</li>
                <li>Use Oreog to develop competing AI services without permission.</li>
              </ul>
              <p>
                Violations may result in suspension or permanent termination of your account without refund.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>10. Content Ownership & Usage Rights</h2>
              <p><strong>Ownership of Output</strong></p>
              <p>
                Subject to the terms of our third-party AI providers, you retain the rights to the content you lawfully create using Oreog (the "Output"), including for commercial purposes. These rights are granted unless restricted by the terms of the specific AI provider whose technology generated the content.
              </p>
              <p><strong>Third-Party Provider Terms</strong></p>
              <p>
                Oreog integrates multiple AI models, including but not limited to those from OpenAI, Anthropic, Google, Perplexity, and other providers. Your use of Outputs is also subject to each provider's terms of service, which you agree to follow.
              </p>
              <p><strong>Intellectual Property Clearance</strong></p>
              <p>
                We make no warranties or guarantees that Output is free from copyright or other intellectual property claims, particularly for image generation, audio transcription, or any media file outputs. It is your sole responsibility to ensure Outputs do not infringe upon third-party rights before use or publication.
              </p>
              <p><strong>Limited License to Oreog</strong></p>
              <p>
                By using Oreog, you grant us a non-exclusive, royalty-free, worldwide license to store, process, and use your prompts and Outputs solely for the purpose of providing and improving our services. We do not claim ownership of your Outputs.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>11. Disclaimer of Third-Party Services</h2>
              <p>
                Oreog integrates AI technologies from third-party providers (e.g., OpenAI, Anthropic, Google, Perplexity AI, etc.). We do not claim ownership over their outputs, and their respective terms & policies also apply to your use of certain features.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>12. Limitation of Liability</h2>
              <p>
                We provide the platform on an "as is" basis without warranties of any kind. To the extent permitted by law, Oreog Labs Inc. will not be liable for any damages (direct, indirect, incidental, or consequential) resulting from the use or inability to use our services.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>13. Age & Parental Consent</h2>
              <p>
                Oreog is not directed towards children under the minimum digital consent age in their country (13 in the US; 16 in the EU unless lowered by law).
              </p>
              <p>
                By using this platform, you confirm that you meet the legal minimum age in your jurisdiction or have obtained verifiable parental consent.
              </p>
              <p>
                We do not knowingly collect personal data from children under these ages without consent. If we discover that such data has been provided, we will delete it promptly.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>14. Termination</h2>
              <p>
                We reserve the right to suspend or terminate accounts that violate these Terms. No refunds will be given for terminations due to violations.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>15. Governing Law & Dispute Resolution</h2>
              <p>
                These Terms are governed by the laws of Delaware, USA. Any disputes will be resolved through binding arbitration, except where prohibited by law.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>16. Contact Us</h2>
              <p>
                For questions or support contact:
              </p>
              <p className="font-medium">
                Email: <a href="mailto:talk.oreog@gmail.com" className="underline hover:text-blue-400">talk.oreog@gmail.com</a>
              </p>
              <p className="mt-4 text-xs">
                © 2026 Oreog. All rights reserved.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
